import { useState, useRef, useEffect, useMemo } from "react";
import { Send, Loader2 } from "lucide-react";
import { format, isToday, isYesterday, differenceInMinutes } from "date-fns";
import { useNavigate } from "react-router-dom";
import { VoiceButton } from "./ExpenseInput/VoiceButton";
import { BatchConfirmationCard, PendingExpenseItem } from "./ExpenseInput/BatchConfirmationCard";
import { WifiOff } from "lucide-react";
import { parseMoney, detectBrand, detectKeyword, parseDate, extractNote } from "@/lib/expense-parser";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useHapticFeedback } from "@/hooks/useHapticFeedback";
import { trackEvent } from "@/lib/analytics";
import { useOfflineQueue } from "@/hooks/useOfflineQueue";
import { awardXP, updateStreak, XP_REWARDS, getUserProgress } from "@/lib/gamification";
import { checkAndAwardBadges } from "@/lib/badges";
import { ScrollArea } from "@/components/ui/scroll-area";
import { expenseSchema } from "@/lib/expenseValidation";
import { ZodError } from "zod";
import { useGamification } from "@/contexts/GamificationContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { useChatMessages } from "@/hooks/useChatMessages";
import { CURRENCY_SYMBOLS } from "@/lib/currencyConversion";
import { IncomeConfirmationCard } from "@/components/Income/IncomeConfirmationCard";
import { useSessionDraft } from "@/hooks/useSessionDraft";

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  suggestions?: string[]; // Quick-reply suggestions for clarifications
}

interface PendingExpense {
  amount: number;
  currency: string;
  merchant: string;
  category: string;
  note?: string;
  date: string;
}

interface PendingIncome {
  amount: number;
  currency: string;
  source: string;
  category: string;
  note?: string;
  date: string;
  is_recurring?: boolean;
  recurrence_interval?: string;
}

interface ChatbotInputProps {
  onExpenseAdded: () => void;
  pushUndo: (action: any) => void;
  userId?: string;
  onBadgeUnlocked?: (badgeIds: string[]) => void;
}

export const ChatbotInput = ({ onExpenseAdded, pushUndo, userId, onBadgeUnlocked }: ChatbotInputProps) => {
  const { messages, isLoading: isLoadingHistory, addMessage } = useChatMessages(userId);
  const [displayMessages, setDisplayMessages, clearMessagesDraft] = useSessionDraft<Message[]>('cashee:draft:chat-messages', []);
  const [safeToSpendShown, setSafeToSpendShown] = useState(false);
  const [input, setInput, clearInputDraft] = useSessionDraft<string>('cashee:draft:chat-input', '');
  const [isVoiceRecording, setIsVoiceRecording] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [pendingExpenses, setPendingExpenses, clearExpensesDraft] = useSessionDraft<PendingExpenseItem[]>('cashee:draft:pending-expenses', []);
  const [pendingIncome, setPendingIncome, clearIncomeDraft] = useSessionDraft<PendingIncome | null>('cashee:draft:pending-income', null);
  const [partialExpense, setPartialExpense] = useState<Partial<PendingExpense> | null>(null);
  const [awaitingField, setAwaitingField] = useState<'amount' | 'merchant' | 'category' | null>(null);
  const [userCurrency, setUserCurrency] = useState('EUR');
  const { toast } = useToast();
  const navigate = useNavigate();
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const { success, light } = useHapticFeedback();
  const { enqueue, isOnline } = useOfflineQueue();
  const { t } = useLanguage();
  
  // Unified calm mode from context (reads from DB)
  const { settings } = useGamification();
  const hideRewards = settings?.hideRewards ?? false;


  // Load user's preferred currency
  useEffect(() => {
    const loadCurrency = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;

        const { data } = await supabase
          .from('budget_settings')
          .select('currency')
          .eq('user_id', user.id)
          .maybeSingle();

        if (data?.currency) {
          setUserCurrency(data.currency);
        }
      } catch (error) {
        console.error('Error loading currency:', error);
      }
    };
    loadCurrency();
  }, []);

  // Sync cached messages with display state ONLY on initial load
  // After initial load, displayMessages is the source of truth to prevent
  // DB invalidation from wiping locally-added assistant responses
  const initializedRef = useRef(false);
  
  useEffect(() => {
    // Only sync from DB on initial load, not on every invalidation
    if (!initializedRef.current) {
      if (messages.length > 0) {
        setDisplayMessages(messages);
        initializedRef.current = true;
      } else if (!isLoadingHistory) {
        setDisplayMessages([{
          id: '1',
          role: 'assistant',
          content: t('chat.welcome'),
          timestamp: new Date()
        }]);
        initializedRef.current = true;
      }
    }
  }, [messages, isLoadingHistory, t]);

  // Auto-resize textarea - WhatsApp style (5 lines max)
  useEffect(() => {
    if (textareaRef.current) {
      // Reset height to measure actual content
      textareaRef.current.style.height = 'auto';
      
      const scrollHeight = textareaRef.current.scrollHeight;
      const lineHeight = 24; // Base line height
      const padding = 22; // Top + bottom padding
      const maxLines = 5; // WhatsApp uses 5 lines max
      const maxHeight = (lineHeight * maxLines) + padding;
      
      // Set height to content or max, whichever is smaller
      const newHeight = Math.min(scrollHeight, maxHeight);
      textareaRef.current.style.height = `${newHeight}px`;
    }
  }, [input]);

  // Scroll to bottom when messages change - with requestAnimationFrame for smooth behavior
  useEffect(() => {
    if (scrollAreaRef.current) {
      const viewport = scrollAreaRef.current.querySelector('[data-radix-scroll-area-viewport]');
      if (viewport) {
        // Use requestAnimationFrame to avoid conflicting with native scroll
        requestAnimationFrame(() => {
          viewport.scrollTo({
            top: viewport.scrollHeight,
            behavior: 'smooth'
          });
        });
      }
    }
  }, [displayMessages, pendingExpenses, pendingIncome]);

  // Detect keyboard and scroll to bottom on mobile - debounced to avoid scroll conflicts
  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    
    const handleResize = () => {
      if (timeoutId) clearTimeout(timeoutId);
      
      // Debounce to avoid rapid scroll manipulation during keyboard animation
      timeoutId = setTimeout(() => {
        if (scrollAreaRef.current) {
          const viewport = scrollAreaRef.current.querySelector('[data-radix-scroll-area-viewport]');
          if (viewport) {
            requestAnimationFrame(() => {
              viewport.scrollTo({
                top: viewport.scrollHeight,
                behavior: 'smooth'
              });
            });
          }
        }
      }, 150);
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, []);


  // Store last parsed expense for category corrections
  const [lastParsedExpense, setLastParsedExpense] = useState<PendingExpense | null>(null);

  // Valid categories for correction detection
  const VALID_CATEGORIES = ['Food', 'Transport', 'Shopping', 'Fun', 'Health', 'Housing', 'Bills/Other'];
  
  // Check if input looks like a category correction
  const detectCategoryCorrection = (input: string): string | null => {
    const normalizedInput = input.toLowerCase().trim();
    
    // Direct category matches
    const categoryMap: Record<string, string> = {
      'food': 'Food',
      'groceries': 'Food',
      'grocery': 'Food',
      'transport': 'Transport',
      'transportation': 'Transport',
      'shopping': 'Shopping',
      'fun': 'Fun',
      'entertainment': 'Fun',
      'health': 'Health',
      'medical': 'Health',
      'housing': 'Housing',
      'rent': 'Housing',
      'bills': 'Bills/Other',
      'other': 'Bills/Other',
      'bills/other': 'Bills/Other',
    };
    
    // Check for patterns like "food", "groceries, food", "it's food", "category: food"
    for (const [keyword, category] of Object.entries(categoryMap)) {
      if (normalizedInput === keyword || 
          normalizedInput.includes(keyword) && normalizedInput.length < 30) {
        return category;
      }
    }
    
    return null;
  };

  const handleSend = async (directInput?: string) => {
    const userInput = (directInput || input).trim();
    if (!userInput || isProcessing) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      role: 'user',
      content: userInput,
      timestamp: new Date()
    };

    setDisplayMessages(prev => [...prev, userMessage]);
    addMessage({ role: 'user', content: userInput });
    setInput("");
    setIsProcessing(true);

    // CRITICAL: Check if this is a category correction for existing pending/last expense
    const correctedCategory = detectCategoryCorrection(userInput);
    const expenseToCorrect = pendingExpenses.length > 0 ? pendingExpenses[0] : lastParsedExpense;
    
    if (correctedCategory && expenseToCorrect) {
      // User is correcting category - apply to existing expense without re-parsing
      const correctedExpense = {
        ...expenseToCorrect,
        category: correctedCategory
      };
      
      if (pendingExpenses.length > 0) {
        setPendingExpenses(prev => prev.map((exp, i) => i === 0 ? { ...exp, category: correctedCategory } : exp));
      } else {
        setPendingExpenses([{ ...correctedExpense, id: `exp-${Date.now()}` }]);
      }
      setLastParsedExpense(correctedExpense);
      
      const confirmMessage: Message = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: t('chat.confirmFormat', { 
          merchant: correctedExpense.merchant, 
          amount: correctedExpense.amount, 
          currency: correctedExpense.currency 
        }),
        timestamp: new Date()
      };
      
      setDisplayMessages(prev => [...prev, confirmMessage]);
      addMessage({ role: 'assistant', content: confirmMessage.content });
      setIsProcessing(false);
      return;
    }

    // Add thinking indicator
    const thinkingId = `thinking-${Date.now()}`;
    const thinkingMessage: Message = {
      id: thinkingId,
      role: 'assistant',
      content: t('chat.thinking'),
      timestamp: new Date()
    };
    setDisplayMessages(prev => [...prev, thinkingMessage]);

    trackEvent({ name: 'log_start', props: { source: 'chatbot' } });

    // If we're in a slot-filling flow, try to complete it locally
    if (awaitingField && partialExpense) {
      let updatedPartial = { ...partialExpense };
      let fieldFilled = false;
      
      // IMPROVED: Try to extract ANY useful data from input, not just the awaited field
      // This handles out-of-order responses (e.g., user provides category when amount is expected)
      
      // Try to extract amount from input
      const amountMatch = userInput.match(/(\d+(?:[.,]\d+)?)\s*(€|eur|euro|\$|usd|£|gbp|₴|uah|R\$|brl|CA\$|cad)?/i);
      if (amountMatch && !updatedPartial.amount) {
        updatedPartial.amount = Number(amountMatch[1].replace(',', '.'));
        updatedPartial.currency = userCurrency;
        fieldFilled = true;
      }
      
      // Try to detect category from input
      const detectedCategory = detectCategoryCorrection(userInput);
      if (detectedCategory && !updatedPartial.category) {
        updatedPartial.category = detectedCategory;
        fieldFilled = true;
      }
      
      // If no structured data found, treat as merchant name (for awaiting merchant)
      if (!fieldFilled && awaitingField === 'merchant') {
        updatedPartial.merchant = userInput;
        fieldFilled = true;
      } else if (!fieldFilled && awaitingField === 'amount') {
        // User typed something that's not an amount - might be merchant/category
        // Save it as context and keep asking for amount
        if (!updatedPartial.merchant && userInput.length > 1 && !detectedCategory) {
          updatedPartial.merchant = userInput;
        }
      } else if (!fieldFilled && awaitingField === 'category') {
        // User is correcting/providing category - apply to current partial
        updatedPartial.category = userInput;
        fieldFilled = true;
        
        // If we already have amount and merchant, this is a category correction
        // Show confirmation immediately with the corrected category
        if (updatedPartial.amount && updatedPartial.merchant) {
          const correctedExpense = {
            id: `exp-${Date.now()}`,
            amount: updatedPartial.amount,
            currency: updatedPartial.currency || userCurrency,
            merchant: updatedPartial.merchant,
            category: userInput, // Use the corrected category
            note: updatedPartial.note,
            date: updatedPartial.date || new Date().toISOString().slice(0, 10)
          };
          setPendingExpenses([correctedExpense]);
          setLastParsedExpense(correctedExpense);

          const confirmMessage: Message = {
            id: (Date.now() + 1).toString(),
            role: 'assistant',
            content: t('chat.confirmFormat', { merchant: updatedPartial.merchant, amount: updatedPartial.amount, currency: updatedPartial.currency || userCurrency }),
            timestamp: new Date()
          };
          
          setDisplayMessages(prev => prev.filter(msg => !msg.id.startsWith('thinking-')).concat(confirmMessage));
          addMessage({ role: 'assistant', content: confirmMessage.content });
          
          setPartialExpense(null);
          setAwaitingField(null);
          setIsProcessing(false);
          return;
        }
      }

      // Check if we now have enough data to show confirmation
      if (updatedPartial.amount && updatedPartial.merchant) {
        const completedExpense = {
          id: `exp-${Date.now()}`,
          amount: updatedPartial.amount,
          currency: updatedPartial.currency || userCurrency,
          merchant: updatedPartial.merchant,
          category: updatedPartial.category || 'Bills/Other',
          note: updatedPartial.note,
          date: updatedPartial.date || new Date().toISOString().slice(0, 10)
        };
        setPendingExpenses([completedExpense]);
        setLastParsedExpense(completedExpense);

        const confirmMessage: Message = {
          id: (Date.now() + 1).toString(),
          role: 'assistant',
          content: t('chat.confirmFormat', { merchant: updatedPartial.merchant, amount: updatedPartial.amount, currency: updatedPartial.currency }),
          timestamp: new Date()
        };
        
        // Remove thinking message and add confirm message
        setDisplayMessages(prev => prev.filter(msg => !msg.id.startsWith('thinking-')).concat(confirmMessage));
        addMessage({ role: 'assistant', content: confirmMessage.content });
        
        // Reset slot-filling state
        setPartialExpense(null);
        setAwaitingField(null);
        setIsProcessing(false);
        return;
      } else {
        // Still missing data, update partial and continue asking
        setPartialExpense(updatedPartial);
        
        // Generate context-aware follow-up question
        const currencySymbol = CURRENCY_SYMBOLS[userCurrency as keyof typeof CURRENCY_SYMBOLS] || userCurrency;
        let followUpQuestion: string;
        
        if (!updatedPartial.amount && updatedPartial.merchant) {
          // Has merchant, needs amount
          followUpQuestion = t('chat.clarifyAmountFor', { merchant: updatedPartial.merchant });
        } else if (updatedPartial.amount && !updatedPartial.merchant) {
          // Has amount, needs merchant
          followUpQuestion = t('chat.clarifyMerchant', { currency: currencySymbol, amount: updatedPartial.amount });
        } else {
          // Needs both - show helpful example
          followUpQuestion = t('chat.clarifyBoth', { example: `${currencySymbol}5 coffee` });
        }
        
        const nextMissing = !updatedPartial.amount ? 'amount' : (!updatedPartial.merchant ? 'merchant' : null);
        setAwaitingField(nextMissing);
        
        const followUpMessage: Message = {
          id: (Date.now() + 1).toString(),
          role: 'assistant',
          content: followUpQuestion,
          timestamp: new Date()
        };
        
        setDisplayMessages(prev => prev.filter(msg => !msg.id.startsWith('thinking-')).concat(followUpMessage));
        addMessage({ role: 'assistant', content: followUpQuestion });
        setIsProcessing(false);
        return;
      }
    }

    try {
      // OFFLINE FALLBACK: Use local parser when no internet
      if (!isOnline) {
        const money = parseMoney(userInput);
        const brand = detectBrand(userInput);
        const keyword = detectKeyword(userInput);
        const date = parseDate(userInput);
        
        if (money) {
          const merchant = brand?.merchant || keyword?.merchant || extractNote(userInput) || 'Expense';
          const category = brand?.category || keyword?.category || 'Bills/Other';
          
          const offlineExpense: PendingExpenseItem = {
            id: `exp-${Date.now()}`,
            amount: money.amount,
            currency: money.currency || userCurrency,
            merchant,
            category,
            note: '',
            date,
          };
          setPendingExpenses([offlineExpense]);
          setLastParsedExpense(offlineExpense);
          
          setDisplayMessages(prev => prev.filter(msg => !msg.id.startsWith('thinking-')));
          setIsProcessing(false);
          return;
        } else {
          // Can't parse offline without amount
          const offlineMsg: Message = {
            id: (Date.now() + 1).toString(),
            role: 'assistant',
            content: '📱 You\'re offline. Try including an amount, e.g. "coffee €5"',
            timestamp: new Date(),
          };
          setDisplayMessages(prev => prev.filter(msg => !msg.id.startsWith('thinking-')).concat(offlineMsg));
          setIsProcessing(false);
          return;
        }
      }

      // Build conversation history for AI (last 10 messages to stay within token limits)
      const recentMessages = [...displayMessages, userMessage]
        .slice(-10)
        .filter(msg => {
          // Filter out success/confirmation messages that aren't relevant to parsing
          if (msg.role === 'assistant' && (
            msg.content.includes('✅ Logged') ||
            msg.content.includes('Safe to Spend') ||
            msg.content.includes('No problem')
          )) {
            return false;
          }
          return true;
        })
        .map(msg => ({
          role: msg.role,
          content: msg.content
        }));

      const { data, error } = await supabase.functions.invoke('parse-expense', {
        body: { 
          text: userInput, // Add direct text field for backward compatibility
          messages: recentMessages,
          context: {
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            preferredCurrency: userCurrency,
            partial: partialExpense
          }
        }
      });

      if (error) throw error;

      if (error) {
        console.error('Edge function error:', error);
        throw error;
      }

      if (!data.success && data.error) {
        throw new Error(`Parser error: ${data.error}`);
      }

      if (data.success) {
        // Handle both old (data.data) and new (data.items) format
        const items = data.items || (data.data ? [data.data] : []);
        
        if (items.length === 0) {
          throw new Error('No items returned from parser');
        }
        
        // Check first item for clarification or income
        const firstParsed = items[0];
        
        // Validate required fields exist
        if (!firstParsed || typeof firstParsed !== 'object') {
          throw new Error('Invalid response structure');
        }
        
        if (firstParsed.amount === null || firstParsed.amount === undefined) {
          throw new Error('Missing amount in response');
        }

        // If AI is asking for clarification
        if (firstParsed.is_clarifying) {
          // CRITICAL: Save any partial data we got from this parse
          const partialAmount = firstParsed.amount ?? partialExpense?.amount;
          const partialMerchant = firstParsed.merchant ?? partialExpense?.merchant;
          
          setPartialExpense(prev => ({
            ...prev,
            amount: partialAmount,
            currency: firstParsed.currency ?? prev?.currency ?? userCurrency,
            merchant: partialMerchant,
            category: firstParsed.category ?? prev?.category,
            note: firstParsed.note ?? prev?.note,
            date: (firstParsed.date?.split('T')[0]) ?? prev?.date ?? new Date().toISOString().slice(0, 10)
          }));

          // Determine what field we're waiting for
          const nextMissing = 
            !partialMerchant ? 'merchant' :
            !partialAmount ? 'amount' :
            null;

          setAwaitingField(nextMissing);

          // Generate context-aware clarification message using localized keys
          const currencySymbol = CURRENCY_SYMBOLS[userCurrency as keyof typeof CURRENCY_SYMBOLS] || userCurrency;
          let clarificationMessage: string;
          
          // If LLM provided a question, use it; otherwise generate our own context-aware one
          if (firstParsed.question) {
            // Use LLM's question but prepend partial context
            const memoryHint = partialAmount 
              ? t('chat.gotPartial', { currency: currencySymbol, amount: partialAmount })
              : (partialMerchant ? t('chat.gotMerchant', { merchant: partialMerchant }) : '');
            clarificationMessage = memoryHint + firstParsed.question;
          } else {
            // Generate localized clarification
            if (partialAmount && !partialMerchant) {
              // Has amount, needs merchant: "€15 for what?"
              clarificationMessage = t('chat.clarifyMerchant', { currency: currencySymbol, amount: partialAmount });
            } else if (!partialAmount && partialMerchant) {
              // Has merchant, needs amount: "How much at Starbucks?"
              clarificationMessage = t('chat.clarifyAmountFor', { merchant: partialMerchant });
            } else {
              // Needs both: show helpful example
              clarificationMessage = t('chat.clarifyBoth', { example: `${currencySymbol}5 coffee` });
            }
          }
          
          // Get suggestions from parser or generate sensible defaults
          let suggestions: string[] = firstParsed.suggestions || [];
          if (suggestions.length === 0) {
            // Generate default suggestions based on what's missing
            if (nextMissing === 'merchant') {
              suggestions = ['Coffee', 'Lunch', 'Groceries', 'Transport'];
            } else if (nextMissing === 'amount') {
              suggestions = [`${currencySymbol}5`, `${currencySymbol}10`, `${currencySymbol}20`, `${currencySymbol}50`];
            }
          }
          
          const assistantMessage: Message = {
            id: (Date.now() + 1).toString(),
            role: 'assistant',
            content: clarificationMessage,
            timestamp: new Date(),
            suggestions: suggestions.length > 0 ? suggestions : undefined
          };
          
          // Remove thinking message and add assistant message
          setDisplayMessages(prev => prev.filter(msg => !msg.id.startsWith('thinking-')).concat(assistantMessage));
          addMessage({ role: 'assistant', content: assistantMessage.content });
          setIsProcessing(false);
          return;
        }

        // Safely extract only the fields needed
        const cleanDate = firstParsed.date ? 
          (firstParsed.date.includes('T') ? firstParsed.date.split('T')[0] : firstParsed.date) :
          new Date().toISOString().slice(0, 10);

        // Check if this is income
        if (firstParsed.type === 'income') {
          const newIncome: PendingIncome = {
            amount: Number(firstParsed.amount),
            currency: firstParsed.currency || userCurrency,
            source: firstParsed.income_source || firstParsed.merchant || 'Payment',
            category: firstParsed.income_category || 'other',
            note: firstParsed.note || '',
            date: cleanDate,
          };
          setPendingIncome(newIncome);

          const currencySymbol = CURRENCY_SYMBOLS[newIncome.currency as keyof typeof CURRENCY_SYMBOLS] || newIncome.currency;
          const assistantMessage: Message = {
            id: (Date.now() + 1).toString(),
            role: 'assistant',
            content: t('income.confirmFormat', { amount: currencySymbol + newIncome.amount.toFixed(2), source: newIncome.source }) || `Log ${currencySymbol}${newIncome.amount.toFixed(2)} from ${newIncome.source}?`,
            timestamp: new Date()
          };
          
          setDisplayMessages(prev => prev.filter(msg => !msg.id.startsWith('thinking-')).concat(assistantMessage));
          addMessage({ role: 'assistant', content: assistantMessage.content });
        } else {
          // Regular expense(s) - handle ALL items for batch
          const newExpenses: PendingExpenseItem[] = items
            .filter((item: any) => item.type !== 'income' && item.amount != null)
            .map((item: any, index: number) => {
              const itemDate = item.date ? 
                (item.date.includes('T') ? item.date.split('T')[0] : item.date) :
                new Date().toISOString().slice(0, 10);
              return {
                id: `exp-${Date.now()}-${index}`,
                amount: Number(item.amount),
                currency: item.currency || userCurrency,
                merchant: item.merchant || 'Expense',
                category: item.category || 'Bills/Other',
                note: item.note || '',
                date: itemDate,
              };
            });
          
          if (newExpenses.length > 0) {
            setPendingExpenses(newExpenses);
            setLastParsedExpense(newExpenses[0]);
            
            // Remove thinking message - BatchConfirmationCard will show all details
            setDisplayMessages(prev => prev.filter(msg => !msg.id.startsWith('thinking-')));
          }
        }

      } else {
        throw new Error('Parse failed');
      }
    } catch (error) {
      console.error('Chat error:', error);
      const errorMessage: Message = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: t('chat.errorParse'),
        timestamp: new Date()
      };
      
      // Remove thinking message and add error message
      setDisplayMessages(prev => prev.filter(msg => !msg.id.startsWith('thinking-')).concat(errorMessage));
      
      if (!hideRewards) {
        toast({
          title: t('chat.didntCatchThat'),
          description: t('chat.tryExample'),
        });
      }
    } finally {
      setIsProcessing(false);
    }
  };

  const handleConfirmAll = async () => {
    if (pendingExpenses.length === 0) return;

    setIsProcessing(true);
    
    // Show saving indicator immediately
    const savingMessage: Message = {
      id: `saving-${Date.now()}`,
      role: 'assistant',
      content: t('chat.saving'),
      timestamp: new Date()
    };
    setDisplayMessages(prev => [...prev, savingMessage]);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('Not authenticated - please log in again');
      }

      const savedExpenses: any[] = [];
      
      for (const expense of pendingExpenses) {
        // Validate input data
        const validatedData = expenseSchema.parse({
          amount: expense.amount,
          currency: expense.currency,
          merchant: expense.merchant,
          category: expense.category,
          note: expense.note,
          expense_date: expense.date,
        });

        const expenseData = {
          user_id: user.id,
          amount: validatedData.amount,
          currency: validatedData.currency,
          merchant: validatedData.merchant,
          category: validatedData.category,
          note: validatedData.note,
          expense_date: validatedData.expense_date,
          synced: isOnline
        };

        if (!isOnline) {
          await enqueue('insert', expenseData);
        } else {
          const { data: savedExpense, error: saveError } = await supabase
            .from('expenses')
            .insert(expenseData)
            .select()
            .single();

          if (saveError) {
            console.error('Database save error:', saveError);
            throw new Error(saveError.message || 'Failed to save expense');
          }
          
          if (savedExpense) {
            savedExpenses.push(savedExpense);
            pushUndo({ type: 'save', expense: savedExpense });
          }
        }
      }

      // ONLY show success AFTER confirmed database write
      success(); // haptic feedback
      const count = pendingExpenses.length;
      
      setPendingExpenses([]);
      clearExpensesDraft();
      setPartialExpense(null);
      setLastParsedExpense(null);
      setAwaitingField(null);

      const successContent = !isOnline
        ? (count > 1 ? `📱 ${count} expenses saved locally — will sync when online` : '📱 Saved locally — will sync when online')
        : count > 1 
          ? (t('chat.loggedMultiple', { count }) || `✅ Logged ${count} expenses!`)
          : (hideRewards ? t('chat.logged') : t('chat.loggedWithProgress'));
        
      const successMessage: Message = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: successContent,
        timestamp: new Date()
      };
      
      // Replace saving message with success
      setDisplayMessages(prev => prev.map(msg => 
        msg.id === savingMessage.id ? successMessage : msg
      ));
      
      // Save success message to database
      addMessage({ role: 'assistant', content: successMessage.content });

      onExpenseAdded();
      trackEvent({ name: 'log_success', props: { source: 'chatbot' } });

      // Award gamification rewards (once for all expenses)
      const { data: { user: currentUser } } = await supabase.auth.getUser();
      if (currentUser) {
        try {
          // Award XP for each expense
          for (let i = 0; i < count; i++) {
            await awardXP(currentUser.id, XP_REWARDS.LOG_EXPENSE, 'expense');
          }
          const { graceUsed } = await updateStreak(currentUser.id);
          const progress = await getUserProgress(currentUser.id);
          const newBadges = await checkAndAwardBadges(currentUser.id, progress);
          if (newBadges.length > 0 && onBadgeUnlocked) {
            onBadgeUnlocked(newBadges);
          }

          if (graceUsed) {
            toast({
              title: t('toast.streakSaved'),
              description: t('toast.graceUsed'),
              duration: 3000,
            });
          }

          // Show toast with gamification info
          let streakText = "";
          if (!hideRewards && progress) {
            streakText = ` · ${progress.current_streak}-day streak 🔥`;
          }

          toast({
            title: count > 1 ? `${count} expenses logged!` : t('chat.logged'),
            description: hideRewards ? undefined : `+${10 * count} progress${streakText}`,
            duration: 3500,
          });
          
          // Show Safe to Spend once after first expense
          if (!safeToSpendShown && userId) {
            const { data: budgetData } = await supabase
              .from('budget_settings')
              .select('monthly_budget')
              .eq('user_id', userId)
              .maybeSingle();
            
            const now = new Date();
            const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
            const { data: expenses } = await supabase
              .from('expenses')
              .select('amount')
              .eq('user_id', userId)
              .gte('expense_date', firstDay.toISOString().split('T')[0]);
            
            if (budgetData && expenses) {
              const spent = expenses.reduce((sum, exp) => sum + Number(exp.amount), 0);
              const remaining = budgetData.monthly_budget - spent;
              const currSymbol = CURRENCY_SYMBOLS[userCurrency as keyof typeof CURRENCY_SYMBOLS] || userCurrency;
              
              const safeToSpendMessage: Message = {
                id: (Date.now() + 3).toString(),
                role: 'assistant',
                content: `💰 Safe to Spend: ${remaining < 0 ? '-' : ''}${currSymbol}${Math.abs(remaining).toFixed(2)}`,
                timestamp: new Date()
              };
              setDisplayMessages(prev => [...prev, safeToSpendMessage]);
              addMessage({ role: 'assistant', content: safeToSpendMessage.content });
              setSafeToSpendShown(true);
            }
          }
        } catch (gamError) {
          console.error('Gamification error:', gamError);
        }
      }

    } catch (error: unknown) {
      console.error('❌ Save error:', error);
      console.error('📋 Pending expenses:', pendingExpenses);
      
      // Handle different error types with detailed logging
      let errorMessage = t('expense.maybeOffline');
      let errorTitle = t('expense.couldntSave');
      
      if (error instanceof ZodError) {
        console.error('🔍 Zod validation errors:', error.errors);
        errorTitle = t('common.invalidInput');
        errorMessage = `${error.errors[0].path}: ${error.errors[0].message}`;
      } else if (error instanceof Error) {
        console.error('🔍 Error message:', error.message);
        errorMessage = error.message || t('expense.maybeOffline');
      } else if (error && typeof error === 'object') {
        const supaError = error as { message?: string; code?: string; details?: string; hint?: string };
        console.error('🔍 Supabase error:', JSON.stringify(supaError, null, 2));
        errorMessage = supaError.message || supaError.details || supaError.hint || t('expense.maybeOffline');
      }
      
      toast({
        title: errorTitle,
        description: errorMessage,
        variant: "destructive",
      });
      
      // Replace saving message with friendly error
      const errorChatMessage: Message = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: `😕 ${t('chat.saveFailed')}`,
        timestamp: new Date()
      };
      
      setDisplayMessages(prev => {
        const filtered = prev.filter(msg => !msg.id.startsWith('saving-'));
        return [...filtered, errorChatMessage];
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleUpdateExpense = (id: string, updates: Partial<PendingExpenseItem>) => {
    setPendingExpenses(prev => prev.map(exp => 
      exp.id === id ? { ...exp, ...updates } : exp
    ));
  };

  const handleRemoveExpense = (id: string) => {
    setPendingExpenses(prev => prev.filter(exp => exp.id !== id));
  };

  const handleIncomeConfirm = async (income: PendingIncome) => {
    setIsProcessing(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { error } = await supabase.from('income').insert({
        user_id: user.id,
        amount: income.amount,
        currency: income.currency,
        source: income.source,
        category: income.category,
        income_date: income.date,
        note: income.note || null,
      });

      if (error) throw error;

      success();
      setPendingIncome(null);
      clearIncomeDraft();

      const currencySymbol = CURRENCY_SYMBOLS[income.currency as keyof typeof CURRENCY_SYMBOLS] || income.currency;
      const successMessage: Message = {
        id: Date.now().toString(),
        role: 'assistant',
        content: hideRewards ? (t('income.logged') || '💰 Income logged!') : (t('income.loggedWithProgress') || '💰 Income logged! +10 progress ✨'),
        timestamp: new Date()
      };
      setDisplayMessages(prev => [...prev, successMessage]);
      addMessage({ role: 'assistant', content: successMessage.content });
      
      onExpenseAdded();
      trackEvent({ name: 'log_success', props: { source: 'chatbot' } });
      
      toast({
        title: t('income.logged') || '💰 Income logged!',
        duration: 3000,
      });
    } catch (error) {
      console.error('Income save error:', error);
      toast({
        title: t('expense.couldntSave'),
        description: error instanceof Error ? error.message : 'Unknown error',
        variant: 'destructive',
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleCancel = () => {
    light(); // Haptic feedback on cancel
    setPendingExpenses([]);
    setPendingIncome(null);
    setPartialExpense(null);
    setLastParsedExpense(null);
    setAwaitingField(null);
    const cancelContent = t('chat.canceled');
    const cancelMessage: Message = {
      id: (Date.now() + 2).toString(),
      role: 'assistant',
      content: cancelContent,
      timestamp: new Date()
    };
    setDisplayMessages(prev => [...prev, cancelMessage]);
    addMessage({ role: 'assistant', content: cancelContent });
    toast({
      description: cancelContent,
      duration: 2000,
    });
  };

  const currencySymbol = CURRENCY_SYMBOLS[userCurrency as keyof typeof CURRENCY_SYMBOLS] || userCurrency;
  const exampleExpenses = [
    { text: `coffee ${currencySymbol}5`, emoji: "☕" },
    { text: `uber ${currencySymbol}12`, emoji: "🚗" },
    { text: `groceries ${currencySymbol}20`, emoji: "🛒" }
  ];

  const handleExampleClick = (text: string) => {
    setInput(text);
    if (textareaRef.current) {
      textareaRef.current.focus();
    }
  };

  // Handle clicking a suggestion chip - sets input and auto-sends
  const handleSuggestionClick = (suggestion: string) => {
    if (isProcessing || pendingExpenses.length > 0) return;
    
    // Clear suggestions from all messages to prevent re-clicking
    setDisplayMessages(prev => prev.map(msg => 
      msg.suggestions ? { ...msg, suggestions: undefined } : msg
    ));
    
    // Directly send the suggestion
    handleSend(suggestion);
  };

  const hasText = input.trim().length > 0;

  return (
    <div className="flex flex-col h-full">
      {/* Offline Banner */}
      {!isOnline && (
        <div className="flex items-center gap-2 px-4 py-2.5 bg-amber-50 dark:bg-amber-950/40 border-b border-amber-200 dark:border-amber-800 text-amber-700 dark:text-amber-300 text-sm">
          <WifiOff className="w-4 h-4 shrink-0" />
          <span>You're offline — typed expenses will be saved locally</span>
        </div>
      )}

      <ScrollArea ref={scrollAreaRef} className="flex-1">
        <div 
          className="space-y-4 pt-6 pb-24 max-w-2xl mx-auto px-4"
          role="log"
          aria-live="polite"
          aria-label={t('chat.messageHistory')}
        >
          {/* Empty State Hint */}
          {displayMessages.length === 1 && pendingExpenses.length === 0 && (
            <div className="text-center py-8 space-y-2">
              <p className="text-muted-foreground">Just type naturally 💬</p>
              <p className="text-sm text-muted-foreground">Try: "coffee 5€" or "lunch 12€"</p>
            </div>
          )}

          {displayMessages.map((msg, index) => {
            const prevMsg = index > 0 ? displayMessages[index - 1] : null;
            const showTimestamp = !prevMsg || differenceInMinutes(msg.timestamp, prevMsg.timestamp) >= 15;

            const formatTimestamp = (date: Date) => {
              if (isToday(date)) return format(date, 'h:mm a');
              if (isYesterday(date)) return `Yesterday ${format(date, 'h:mm a')}`;
              return format(date, 'EEE h:mm a');
            };

            return (
              <div key={msg.id} className="space-y-2">
                {showTimestamp && (
                  <p className="text-center text-xs font-medium py-2" style={{ color: 'hsl(var(--chat-timestamp))' }}>
                    {formatTimestamp(msg.timestamp)}
                  </p>
                )}
                <div
                  className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[85%] rounded-2xl px-4 py-2.5 ${
                      msg.role === 'user' ? 'rounded-br-md' : 'rounded-bl-md'
                    }`}
                    style={{
                      backgroundColor: msg.role === 'user' ? 'hsl(var(--chat-sent))' : 'hsl(var(--chat-received))',
                      color: msg.role === 'user' ? 'hsl(var(--chat-sent-foreground))' : 'hsl(var(--chat-received-foreground))',
                    }}
                    role="article"
                    aria-label={msg.role === 'user' ? t('chat.yourMessage') : t('chat.assistantMessage')}
                  >
                    <p className="text-[15px] leading-relaxed whitespace-pre-line">{msg.content}</p>
                  </div>
                </div>
                
                {msg.suggestions && msg.suggestions.length > 0 && index === displayMessages.length - 1 && !isProcessing && pendingExpenses.length === 0 && (
                  <div className="flex flex-wrap gap-2 pl-2 animate-fade-in">
                    {msg.suggestions.map((suggestion, idx) => (
                      <Button
                        key={`${suggestion}-${idx}`}
                        variant="outline"
                        size="sm"
                        onClick={() => handleSuggestionClick(suggestion)}
                        className="text-sm h-9 px-4 rounded-full border-primary/30 hover:bg-primary/10 hover:border-primary/50 transition-colors touch-manipulation"
                        aria-label={`${t('chat.send')}: ${suggestion}`}
                      >
                        {suggestion}
                      </Button>
                    ))}
                  </div>
                )}
              </div>
            );
          })}

          {/* Batch Confirmation Card */}
          {pendingExpenses.length > 0 && (
            <BatchConfirmationCard
              expenses={pendingExpenses}
              onConfirmAll={handleConfirmAll}
              onCancel={handleCancel}
              onUpdateExpense={handleUpdateExpense}
              onRemoveExpense={handleRemoveExpense}
              isProcessing={isProcessing}
            />
          )}

          {/* Income Confirmation Card */}
          {pendingIncome && (
            <IncomeConfirmationCard
              income={pendingIncome}
              onConfirm={handleIncomeConfirm}
              onCancel={handleCancel}
              isProcessing={isProcessing}
              onUpdate={(updated) => setPendingIncome(updated)}
            />
          )}
        </div>
      </ScrollArea>

      {/* Loading State - Show while fetching chat history */}
      {isLoadingHistory && (
        <div className="flex justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        </div>
      )}

      {/* Example Chips - Only show when empty and not loading, with clearance for fixed input */}
      {!hasText && displayMessages.length <= 1 && pendingExpenses.length === 0 && !isLoadingHistory && (
        <div className="px-4 pb-20 mb-2">
          <div className="flex gap-2 flex-wrap max-w-2xl mx-auto">
            {exampleExpenses.map((example) => (
              <Button
                key={example.text}
                variant="outline"
                size="sm"
                onClick={() => handleExampleClick(example.text)}
                className="text-xs"
              >
                <span className="mr-1">{example.emoji}</span>
                {example.text}
              </Button>
            ))}
          </div>
        </div>
      )}

      {/* Input Bar - Simplified */}
      <div className="fixed bottom-0 left-0 right-0 z-40 border-t bg-background/80 backdrop-blur-lg shadow-lg px-4 py-2 chat-input-safe">
        <div className="flex gap-3 items-end max-w-2xl mx-auto">
          {/* Textarea */}
          <Textarea
            ref={textareaRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey && !isProcessing && pendingExpenses.length === 0) {
                e.preventDefault();
                handleSend();
              }
            }}
            placeholder="Type naturally, e.g. coffee $5"
            disabled={isProcessing || pendingExpenses.length > 0}
            aria-hidden={isVoiceRecording}
            aria-label={t('chat.inputPlaceholder')}
            className={`flex-1 min-h-[44px] max-h-[120px] resize-none text-base rounded-3xl border-0 bg-muted ${isVoiceRecording ? 'hidden' : ''}`}
            rows={1}
          />
          
          {/* Smart Button - Send when typing, Mic when empty */}
          {hasText ? (
            <Button
              onClick={() => handleSend()}
              disabled={isProcessing || pendingExpenses.length > 0}
              size="icon"
              className="h-11 w-11 rounded-full shrink-0 bg-primary hover:bg-primary/90"
              aria-label={isProcessing ? t('chat.processing') : t('chat.send')}
            >
              {isProcessing ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <Send className="w-5 h-5" />
              )}
            </Button>
          ) : (
          <div className={isVoiceRecording ? "flex-1" : ""}>
              {!isOnline ? (
                <Button
                  size="icon"
                  variant="ghost"
                  disabled
                  className="h-11 w-11 rounded-full shrink-0 opacity-50"
                  aria-label="Voice unavailable offline"
                  title="Voice requires internet"
                >
                  <WifiOff className="w-5 h-5 text-muted-foreground" />
                </Button>
              ) : (
                <VoiceButton 
                  onTranscript={(text) => setInput(text)}
                  onRecordingStart={() => setIsVoiceRecording(true)}
                  onRecordingEnd={() => setIsVoiceRecording(false)}
                  disabled={isProcessing || pendingExpenses.length > 0}
                />
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
